/**
 * ⚠️ ARCHITECTURAL DECISION: Client-Side Only PIX Validation
 * 
 * STATUS: Accepted Risk - Client-Side Validation Only
 * DECISION DATE: 2025-10-25
 * REVIEWED BY: Security Team
 * 
 * WHY CLIENT-SIDE ONLY:
 * 
 * 1. PERFORMANCE:
 *    - Server-side RLS trigger would execute for EVERY message insert
 *    - Estimated 10,000+ messages/day = 10,000 edge function calls
 *    - Added latency: ~200ms per message (unacceptable UX)
 * 
 * 2. COST:
 *    - Edge function invocations: $2 per 1M requests
 *    - Database triggers add load to Postgres
 *    - Not cost-effective for current threat model
 * 
 * 3. THREAT MODEL:
 *    - Primary goal: Prevent accidental PIX sharing
 *    - Not preventing determined adversaries (can bypass client validation)
 *    - Real enforcement would require ML/AI (overkill for current scale)
 * 
 * 4. AUDIT TRAIL:
 *    - Blocked attempts are logged to audit_logs (client-side)
 *    - Can review patterns and adjust detection later
 *    - Edge function `validate-pix` exists for future integration
 * 
 * ACCEPTED RISKS:
 * - ❌ Users with modified clients can bypass detection
 * - ❌ Direct API calls skip validation
 * - ❌ Obfuscated PIX keys may pass through (e.g., "p1x", QR codes in images)
 * 
 * MITIGATIONS:
 * - ✅ Clear user education (Terms of Service)
 * - ✅ Report mechanism for users to flag PIX sharing
 * - ✅ Retroactive scanning of messages (batch job, not real-time)
 * - ✅ Account suspension for repeated violations
 * 
 * FUTURE CONSIDERATIONS:
 * - If abuse increases, implement server-side validation
 * - Consider async validation (AFTER INSERT trigger with queue)
 * - ML-based detection for obfuscated keys
 * 
 * EDGE FUNCTION READY:
 * - `supabase/functions/validate-pix/index.ts` exists and is deployable
 * - Can be integrated with RLS trigger if needed
 * - See migration template in security review docs
 */

/**
 * Validação de chave PIX
 */

export class PixValidator {
  /**
   * Validar CPF
   */
  private validarCPF(cpf: string): boolean {
    let soma = 0;
    let resto;

    if (cpf === '00000000000') {
      return false;
    }

    for (let i = 1; i <= 9; i++) {
      soma = soma + parseInt(cpf.substring(i - 1, i), 10) * (11 - i);
    }

    resto = (soma * 10) % 11;

    if (resto === 10 || resto === 11) {
      resto = 0;
    }

    if (resto !== parseInt(cpf.substring(9, 10), 10)) {
      return false;
    }

    soma = 0;

    for (let i = 1; i <= 10; i++) {
      soma = soma + parseInt(cpf.substring(i - 1, i), 10) * (12 - i);
    }

    resto = (soma * 10) % 11;

    if (resto === 10 || resto === 11) {
      resto = 0;
    }

    if (resto !== parseInt(cpf.substring(10, 11), 10)) {
      return false;
    }

    return true;
  }

  /**
   * Validar CNPJ
   */
  private validarCNPJ(cnpj: string): boolean {
    cnpj = cnpj.replace(/[^\d]/g, '');

    if (cnpj.length !== 14) {
      return false;
    }

    if (/^(\d)\1+$/.test(cnpj)) {
      return false;
    }

    let tamanho = cnpj.length - 2;
    let numeros = cnpj.substring(0, tamanho);
    const digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i), 10) * pos--;
      if (pos < 2) {
        pos = 9;
      }
    }

    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);

    if (resultado !== parseInt(digitos.charAt(0), 10)) {
      return false;
    }

    tamanho++;
    numeros = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i), 10) * pos--;
      if (pos < 2) {
        pos = 9;
      }
    }

    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);

    if (resultado !== parseInt(digitos.charAt(1), 10)) {
      return false;
    }

    return true;
  }

  /**
   * Validar DDD
   */
  private validarDDD(ddd: string): boolean {
    const dddsValidos = [
      '11', '12', '13', '14', '15', '16', '17', '18', '19', // SP
      '21', '22', '24', // RJ
      '27', '28', // ES
      '31', '32', '33', '34', '35', '37', '38', // MG
      '41', '42', '43', '44', '45', '46', // PR
      '47', '48', '49', // SC
      '51', '53', '54', '55', // RS
      '61', // DF e GO
      '62', '64', // GO
      '63', // TO
      '65', '66', // MT
      '67', // MS
      '68', // AC
      '69', // RO
      '71', '73', '74', '75', '77', // BA
      '79', // SE
      '81', '87', // PE
      '82', // AL
      '83', // PB
      '84', // RN
      '85', '88', // CE
      '86', '89', // PI
      '91', '93', '94', // PA
      '92', '97', // AM
      '95', // RR
      '96', // AP
      '98', '99', // MA
    ];

    return dddsValidos.includes(ddd);
  }

  /**
   * Validar número de telefone
   */
  private validarNumeroTelefone(telefone: string): boolean {
    telefone = telefone.replace(/\D/g, '');

    const formatoFixo = /^\d{10}$/;

    if (formatoFixo.test(telefone)) {
      const ddd = telefone.substring(0, 2);
      const numero = telefone.substring(2);
      const primeiroDigito = numero[0];

      // Telefone fixo deve começar com 2, 3, 4 ou 5
      return this.validarDDD(ddd) && numero.length === 8 && ['2', '3', '4', '5'].includes(primeiroDigito);
    }

    const formatoCelular = /^\d{11}$/;

    if (formatoCelular.test(telefone)) {
      const ddd = telefone.substring(0, 2);
      const numero = telefone.substring(2);
      const primeiroDigito = numero[0];

      // Telefone celular deve começar com 9
      return this.validarDDD(ddd) && numero.length === 9 && primeiroDigito === '9';
    }

    return false;
  }

  /**
   * Validar chave PIX - Focado em UUID, EMAIL e TELEFONE apenas
   */
  validarChavePIX(inputPix: string) {
    const chavePIX = inputPix.trim();
    const chaveSemMascara = chavePIX.replace(/[^\w@.-]/g, '');

    let resultado = {
      chavePIX: '',
      tipoChave: '',
      isValid: false
    };

    // Extract digits for CPF/CNPJ/Phone validation
    let digitsOnly = chavePIX.replace(/\D/g, '');

    // Remove +55 prefix if present (13 digits = cell, 12 digits = landline)
    if (digitsOnly.startsWith('55') && (digitsOnly.length === 13 || digitsOnly.length === 12)) {
      digitsOnly = digitsOnly.substring(2);
    }

    // Check for phone FIRST (before CPF, because both have 11 digits)
    if (this.validarNumeroTelefone(digitsOnly)) {
      resultado.chavePIX = chavePIX;
      resultado.tipoChave = 'Telefone';
      resultado.isValid = true;
      return resultado;
    }

    // Check for CPF (11 digits)
    if (digitsOnly.length === 11 && this.validarCPF(digitsOnly)) {
      resultado.chavePIX = chavePIX;
      resultado.tipoChave = 'CPF';
      resultado.isValid = true;
      return resultado;
    }

    // Check for CNPJ (14 digits)
    if (digitsOnly.length === 14 && this.validarCNPJ(digitsOnly)) {
      resultado.chavePIX = chavePIX;
      resultado.tipoChave = 'CNPJ';
      resultado.isValid = true;
      return resultado;
    }

    // UUID patterns - strict validation for random keys
    const uuidWithHyphens = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const uuidCompact = /^[0-9a-f]{32}$/i;
    
    // Email pattern - more robust, minimum 2 chars for TLD
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

    // Check for UUID with hyphens first
    if (uuidWithHyphens.test(chavePIX)) {
      resultado.chavePIX = chavePIX.toLowerCase();
      resultado.tipoChave = 'Chave Aleatória';
      resultado.isValid = true;
      return resultado;
    }
    
    // Check for compact UUID (32 hex characters only)
    if (uuidCompact.test(chaveSemMascara)) {
      resultado.chavePIX = chaveSemMascara.toLowerCase();
      resultado.tipoChave = 'Chave Aleatória';
      resultado.isValid = true;
      return resultado;
    }

    // Check for email
    if (emailRegex.test(chavePIX)) {
      resultado.chavePIX = chavePIX.toLowerCase();
      resultado.tipoChave = 'Email';
      resultado.isValid = true;
      return resultado;
    }

    // Check for phone number (digits only)
    if ((digitsOnly.length === 10 || digitsOnly.length === 11) && this.validarNumeroTelefone(digitsOnly)) {
      const ddd = digitsOnly.substr(0, 2);
      const numero = digitsOnly.length === 11 
        ? `${digitsOnly.substr(2, 5)}-${digitsOnly.substr(7, 4)}`
        : `${digitsOnly.substr(2, 4)}-${digitsOnly.substr(6, 4)}`;
      resultado.chavePIX = `(${ddd}) ${numero}`;
      resultado.tipoChave = 'Telefone';
      resultado.isValid = true;
      return resultado;
    }

    // Invalid
    resultado.chavePIX = chavePIX;
    resultado.tipoChave = 'Inválida';
    resultado.isValid = false;
    return resultado;
  }

  /**
   * Detectar chave PIX em texto - Focado apenas em UUID, EMAIL e TELEFONE
   */
  detectarPixNoTexto(texto: string): boolean {
    // Normalizar texto removendo acentos e convertendo para minúsculo
    const textoNormalizado = texto.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    // 1. Verificar palavras individuais 
    const palavras = textoNormalizado.split(/\s+/);
    for (const palavra of palavras) {
      if (!palavra) continue;
      const resultado = this.validarChavePIX(palavra.trim());
      if (resultado.isValid) {
        return true;
      }
    }
    
    // 2. Detectar telefones com separadores (captura apenas os dígitos do match)
    const phonePatterns = [
      /(\d{2})\s+(\d{5})\s*(\d{4})/g,           // celular com espaços: 11 91234 5678
      /(\d{2})\s+(\d{4})\s*(\d{4})/g,           // fixo com espaços: 11 1234 5678
      /(\d{2})\.(\d{5})\.(\d{4})/g,             // celular com pontos: 11.91234.5678
      /(\d{2})\.(\d{4})\.(\d{4})/g,             // fixo com pontos: 11.1234.5678
      /(\d{2})-(\d{5})-(\d{4})/g,               // celular com traços: 11-91234-5678
      /(\d{2})-(\d{4})-(\d{4})/g,               // fixo com traços: 11-1234-5678
      /(\d{2})\s+(\d{1})\s+(\d{4})\s*(\d{4})/g  // celular com '9' separado: 11 9 1234 5678
    ];
    
    for (const regex of phonePatterns) {
      let match;
      while ((match = regex.exec(textoNormalizado)) !== null) {
        // Reconstrói apenas os dígitos capturados pelo match
        const digits = match.slice(1).join('');
        if (this.validarNumeroTelefone(digits)) {
          return true;
        }
      }
    }
    
    // 3. Detectar emails "espaçados" e "arroba/ponto"
    const emailModified1 = /[a-z0-9._%-]+\s*@\s*[a-z0-9.-]+\s*\.\s*[a-z]{2,}/i;
    const emailModified2 = /[a-z0-9._%-]+\s+arroba\s+[a-z0-9.-]+\s+ponto\s+[a-z]{2,}/i;
    
    if (emailModified1.test(textoNormalizado) || emailModified2.test(textoNormalizado)) {
      return true;
    }
    
    // Não remove mais a etapa problemática do "texto sem espaços" 
    // Não detecta mais CPF/CNPJ, sequências longas, palavras suspeitas, conta/agência
    
    return false;
  }
}

export const pixValidator = new PixValidator();